import { Controller } from "@hotwired/stimulus";
import { initializeApp } from "firebase/app";
import {
  getAuth,
  RecaptchaVerifier,
  signInWithPhoneNumber,
} from "firebase/auth";

// --- Configuration ---
const FIREBASE_CONFIG = {
  apiKey: "AIzaSyCpTo1XwaPgh4ubES5DfRVfngoZUgpidWg",
  authDomain: "mapmydeal-dealapp.firebaseapp.com",
  projectId: "mapmydeal-dealapp",
  storageBucket: "mapmydeal-dealapp.firebasestorage.app",
  messagingSenderId: "587969660436",
  appId: "1:587969660436:web:aa49a7d60c4711f0ce0901",
  measurementId: "G-16EF1BV0SS",
};

const COUNTRY_CODES = [
  { code: "+91", country: "IN", flag: "🇮🇳", name: "India" },
  { code: "+1", country: "US", flag: "🇺🇸", name: "United States" },
  { code: "+44", country: "GB", flag: "🇬🇧", name: "United Kingdom" },
  { code: "+971", country: "AE", flag: "🇦🇪", name: "UAE" },
  { code: "+65", country: "SG", flag: "🇸🇬", name: "Singapore" },
  { code: "+61", country: "AU", flag: "🇦🇺", name: "Australia" },
  { code: "+86", country: "CN", flag: "🇨🇳", name: "China" },
];

// Initialize Firebase
const app = initializeApp(FIREBASE_CONFIG);
const auth = getAuth(app);
auth.useDeviceLanguage();

export default class extends Controller {
  static targets = [
    "phoneStep",
    "otpStep",
    "phoneInput",
    "countryCodeBtn",
    "countryDropdown",
    "countryList",
    "submitBtn",
    "submitBtnText",
    "submitBtnLoader",
    "otpInput",
    "otpLoader",
    "userPhoneNumberDisplay",
  ];

  static values = {
    currentCountry: { type: Object, default: COUNTRY_CODES[0] },
    phoneNumber: String,
  };

  connect() {
    this.renderCountryList();
    this.updateCountryButton();
    this.setupRecaptcha();
    this.isVerifying = false; // Initialize the verification guard flag
  }

  // --- Firebase & Auth Logic ---

  setupRecaptcha() {
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(auth, "sign-in-button", {
        size: "invisible",
      });
    }
  }

  async submitPhone(event) {
    if (event) event.preventDefault();

    const rawPhone = this.phoneInputTarget.value;
    const fullPhoneNumber = `${this.currentCountryValue.code}${rawPhone.replace(/\D/g, "")}`;

    if (rawPhone.length < 10) return;

    this.setLoading(true);

    try {
      const appVerifier = window.recaptchaVerifier;

      this.confirmationResult = await signInWithPhoneNumber(
        auth,
        fullPhoneNumber,
        appVerifier,
      );

      this.setLoading(false);
      this.transitionToOtp();
    } catch (error) {
      console.error("SMS Error:", error);
      this.setLoading(false);

      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.render().then((widgetId) => {
          grecaptcha.reset(widgetId);
        });
      }
    }
  }

  async verifyOtp() {
    // Guard clause to prevent multiple submissions (e.g., from rapid pasting)
    if (this.isVerifying) return;

    const code = this.otpInputTargets.map((i) => i.value).join("");
    if (code.length !== 6) return;

    this.isVerifying = true; // Lock verification
    this.otpLoaderTarget.classList.remove("hidden");

    try {
      const result = await this.confirmationResult.confirm(code);
      const user = result.user;
      const idToken = await user.getIdToken();
      await this.createRailsSession(idToken);
    } catch (error) {
      console.error("Verification Error:", error);
      alert("Invalid code. Please try again.");
      this.otpLoaderTarget.classList.add("hidden");
      this.otpInputTargets.forEach((input) => (input.value = ""));
      this.otpInputTargets[0].focus();
    } finally {
      this.isVerifying = false; // Unlock verification whether it succeeds or fails
    }
  }

  async createRailsSession(idToken) {
    const csrfToken = document.querySelector("[name='csrf-token']")?.content;

    try {
      const response = await fetch("/user_sessions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({ token: idToken }),
      });

      if (response.ok) {
        window.location.href = "/";
      } else {
        throw new Error("Server verification failed");
      }
    } catch (error) {
      console.error("Rails Session Error:", error);
      this.otpLoaderTarget.classList.add("hidden");
    }
  }

  // --- UI Logic (Step Switching & Inputs) ---

  transitionToOtp() {
    const raw = this.phoneInputTarget.value;
    const formatted = `${raw.slice(0, 5)} ${raw.slice(5)}`;
    this.userPhoneNumberDisplayTarget.textContent = `${this.currentCountryValue.code} ${formatted}`;

    this.phoneStepTarget.classList.add("hidden");
    this.otpStepTarget.classList.remove("hidden");
    this.otpInputTargets[0].focus();
  }

  backToPhone() {
    this.otpStepTarget.classList.add("hidden");
    this.phoneStepTarget.classList.remove("hidden");
    this.phoneInputTarget.focus();
    this.otpInputTargets.forEach((input) => (input.value = ""));
  }

  formatPhone(event) {
    let value = event.target.value.replace(/\D/g, "").slice(0, 10);
    event.target.value = value;

    const isValid = value.length === 10;
    this.submitBtnTarget.disabled = !isValid;
    this.submitBtnTarget.classList.toggle("opacity-50", !isValid);
    this.submitBtnTarget.classList.toggle("cursor-not-allowed", !isValid);
  }

  handleOtpInput(event) {
    const input = event.target;
    const value = input.value;
    const index = this.otpInputTargets.indexOf(input);

    if (!/^\d*$/.test(value)) {
      input.value = "";
      return;
    }

    // Auto-advance to the next input
    if (value.length === 1 && index < 5) {
      this.otpInputTargets[index + 1].focus();
    }

    const fullCode = this.otpInputTargets.map((i) => i.value).join("");
    if (fullCode.length === 6) {
      this.verifyOtp();
    }
  }

  handleOtpKeydown(event) {
    const input = event.target;
    const index = this.otpInputTargets.indexOf(input);

    // Auto-retreat to the previous input on backspace
    if (event.key === "Backspace" && !input.value && index > 0) {
      this.otpInputTargets[index - 1].focus();
    }
  }

  handlePaste(event) {
    event.preventDefault();
    const paste = (event.clipboardData || window.clipboardData).getData("text");
    const digits = paste.replace(/\D/g, "").slice(0, 6).split("");

    digits.forEach((digit, i) => {
      if (this.otpInputTargets[i]) this.otpInputTargets[i].value = digit;
    });

    if (digits.length > 0) {
      const focusIndex = Math.min(digits.length, 5);
      this.otpInputTargets[focusIndex].focus();
      if (digits.length === 6) this.verifyOtp();
    }
  }

  toggleCountryDropdown() {
    this.countryDropdownTarget.classList.toggle("hidden");
    this.countryCodeBtnTarget
      .querySelector(".chevron")
      .classList.toggle("rotate-180");
  }

  selectCountry(event) {
    const code = event.currentTarget.dataset.code;
    this.currentCountryValue = COUNTRY_CODES.find((c) => c.code === code);
    this.updateCountryButton();
    this.toggleCountryDropdown();
  }

  updateCountryButton() {
    const btn = this.countryCodeBtnTarget;
    btn.querySelector(".flag").textContent = this.currentCountryValue.flag;
    btn.querySelector(".code").textContent = this.currentCountryValue.code;
  }

  renderCountryList() {
    this.countryListTarget.innerHTML = COUNTRY_CODES.map(
      (c) => `
      <button type="button" 
              data-action="click->login#selectCountry"
              data-code="${c.code}"
              class="w-full px-4 py-3 flex items-center gap-3 hover:bg-emerald-50 transition-colors text-left">
        <span class="text-xl">${c.flag}</span>
        <span class="flex-1 text-gray-900">${c.name}</span>
        <span class="text-gray-600 font-medium">${c.code}</span>
      </button>
    `,
    ).join("");
  }

  setLoading(isLoading) {
    if (isLoading) {
      this.submitBtnTextTarget.classList.add("hidden");
      this.submitBtnLoaderTarget.classList.remove("hidden");
    } else {
      this.submitBtnTextTarget.classList.remove("hidden");
      this.submitBtnLoaderTarget.classList.add("hidden");
    }
  }
}
